import React, { useCallback, useMemo, useState } from "react";

import { Button as MaterialButton, Typography, makeStyles } from "@material-ui/core";
import * as firebase from "firebase/app";

import { Label } from "../components/Label";
import { EmailField, PasswordField } from "../components/form/InputField";
import { Button } from "../components/Button";
import { Group } from "../components/form/Group";
import { Hint } from "../components/form/Hint";
import { UserDocument } from "../firebase.model";
import { ResetPasswordDialogProps, useResetPasswordDialog } from "./ResetPasswordDialog";
import { AskForHelpAsGuestForm, AskForHelpAsGuestFormIds, useAskForHelpAsGuestDialog } from "../workflow/ask-for-help/AskForHelpAsGuestDialog";
import { FormItem } from "../components/form-item/framework/core/form-item.model";
import { FormProps } from "../components/form-item/framework/react/FormProvider";
import { FormController } from "../components/form-item/framework/core/form.controller";
import { TextAreaFormItem } from "../components/form-item/client/components";
import { useBlockingProgress } from "../components/blocking-progress";
import { useSnackbar } from "../components/snackbar";
import { GuestActionData, useGuestAction } from "../workflow/guest-action.hook";

const login = firebase.app().functions("europe-west3").httpsCallable("login");
const resetPassword = firebase.app().functions("europe-west3").httpsCallable("User-changePassword");

const useStyles = makeStyles(theme => ({
    smallMargin: {
        marginBottom: theme.spacing(1),
    },
    mediumMargin: {
        marginBottom: theme.spacing(5),
    },
    largeMargin: {
        marginBottom: theme.spacing(6),

        [theme.breakpoints.up(600)]: {
            marginBottom: theme.spacing(9),
        },
    },
    linkButton: {
        display: "block",
        padding: "8px 0",
        margin: "0 auto 8px",

        fontSize: "16px",
        fontWeight: 500,
        color: "#4A4A4A",
        textAlign: "center",
        textDecoration: "none",
        textTransform: "none",

        "&:last-child": {
            marginBottom: 0,
        },

        "&:hover": {
            background: "none",
            textDecoration: "underline",
        },
    },
}));

export const LoginPage = () => {
    const classes = useStyles();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const [dirty, setDirty] = useState({ email: false, password: false });

    const markEmailDirty = useCallback(() => {
        setDirty({ ...dirty, email: true });
    }, [dirty]);

    const markPasswordDirty = useCallback(() => {
        setDirty({ ...dirty, password: true });
    }, [dirty]);

    const validationErrors = useMemo(() => {
        return {
            email: (() => {
                if ( !email ) return "Skal udfyldes";
                if ( !email.match(/\S+@\S+\.\S+/) ) return "Skal være en gyldig email";

                return undefined;
            })(),
            password: (() => {
                if ( !password ) return "Skal udfyldes";
                if ( password.length < 6 ) return "Skal være mindst 6 tegn";

                return undefined;
            })(),
        }
    }, [email, password]);

    const query = new URLSearchParams(window.location.search);
    const returnUrl = query.get("returnUrl");

    const { block, unblock } = useBlockingProgress();
    const { openSnackbar } = useSnackbar();

    const logIn = useCallback((event: React.MouseEvent) => {
        event.preventDefault();

        if ( document.activeElement )
            (document.activeElement as HTMLInputElement).blur();

        if ( validationErrors.email || validationErrors.password )
            return setDirty({ email: true, password: true });

        block();

        firebase.auth().signInWithEmailAndPassword(email, password).then(async auth => {
            if ( !auth.user ) throw new Error("User not available");

            const idToken = await auth.user.getIdToken();
            const loginResult = await login(idToken);

            const userDocument = await firebase.firestore().collection("users").doc(auth.user.uid).get();
            const user = userDocument.exists ? { id: userDocument.id, ...userDocument.data() } as UserDocument : null;
            const type = user?.type || "gardener";

            const parsedUrl = new URL((() => {
                if ( returnUrl ) return returnUrl;

                switch ( type ) {
                    case "gardener": return "https://web.gogogarden.dk";
                    case "administrator": return "https://admin.gogogarden.dk";
                }
            })());

            const allowedDomains = (() => {
                switch ( type ) {
                    case "gardener": return ["localhost", "gardener-cms.web.app", "web.gogogarden.dk", "web.gogogarden.com"];
                    case "administrator": return ["localhost", "gogo-admin.web.app", "admin.gogogarden.dk"];
                }
            })();

            if ( !allowedDomains.includes(parsedUrl.hostname) ) throw new Error("Unexpected host");

            const url = `${parsedUrl.origin}/login?token=${loginResult.data}`;

            window.location.replace(returnUrl ? `${url}&returnUrl=${returnUrl}` : url);
        })
            .catch(error => {
                unblock();

                const message = (() => {
                    try {
                        const actualError = JSON.parse(error.message).error;
                        
                        if ( actualError.message === "INVALID_LOGIN_CREDENTIALS" ) return "Adgangskoden er forkert eller brugeren eksisterer ikke.";
                        
                        console.log(actualError);
                    } catch (_) {
                        console.error(error);
                    }

                    return "Der skete en fejl. Prøv igen.";
                })();

                openSnackbar("error", message);
            });

    }, [validationErrors, email, password, returnUrl, block, unblock, openSnackbar]);

    const emailError = dirty.email && validationErrors.email;
    const passwordError = dirty.password && validationErrors.password;

    const resetPasswordAdapter = useCallback<ResetPasswordDialogProps["onConfirm"]>((viewModel) => {
        block();

        resetPassword({ email: viewModel.email })
            .then(() => {
                openSnackbar("success", "Vi har sendt et link til nulstilling af adgangskode")
                unblock();
            })
            .catch(() => {
                openSnackbar("error", "Der skete en fejl. Vi kunne ikke sende et link til nulstilling af adgangskode.");
                unblock();
            })
    }, [block, unblock, openSnackbar]);
    const openResetPasswordDialog = useResetPasswordDialog(resetPasswordAdapter, {
        email: "",
        emailDirty: false,
    });

    const guestAction = useGuestAction();
    const askForHelpActionAdapter = useCallback<FormProps<AskForHelpAsGuestForm>["onSubmit"]>((controller, form): void => {
        const data = mapAskForHelpFormToTaskResponseData(controller, form);

        guestAction(data);
    }, [guestAction]);

    const openAskForHelpDialog = useAskForHelpAsGuestDialog(askForHelpActionAdapter);

    return (
        <main style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "16px" }}>
            <form style={{ maxWidth: "520px", padding: "8px" }}>
                <Typography color="secondary" className={classes.largeMargin} style={{ fontSize: "30px", fontWeight: 600, textAlign: "center" }}>Velkommen tilbage!</Typography>

                <Label htmlFor="email" className={classes.smallMargin}>Email</Label>

                <Group className={classes.mediumMargin} error={Boolean(emailError)}>
                    <EmailField id="email" value={email} onChange={setEmail} onBlur={markEmailDirty} />
                    {emailError ? <Hint>{emailError}</Hint> : null}
                </Group>

                <Label htmlFor="password" className={classes.smallMargin}>Adgangskode</Label>

                <Group className={classes.largeMargin} error={Boolean(passwordError)}>
                    <PasswordField id="password" value={password} onChange={setPassword} onBlur={markPasswordDirty} />
                    {passwordError ? <Hint>{passwordError}</Hint> : null}
                </Group>

                <Button type="submit" onClick={logIn} className={classes.mediumMargin}>Log ind</Button>

                <MaterialButton className={classes.linkButton} onClick={openResetPasswordDialog} disableRipple>Har du glemt din adgangskode?</MaterialButton>
                <MaterialButton className={classes.linkButton} onClick={openAskForHelpDialog} disableRipple>Bed om hjælp</MaterialButton>
            </form>
        </main>
    );
};

const mapAskForHelpFormToTaskResponseData = (controller: FormController, form: FormItem): GuestActionData => {
    const name = (controller.getItem(AskForHelpAsGuestFormIds.TextAreaName, form) as TextAreaFormItem).value;
    const email = (controller.getItem(AskForHelpAsGuestFormIds.TextAreaEmail, form) as TextAreaFormItem).value;
    const phoneNumber = (controller.getItem(AskForHelpAsGuestFormIds.TextAreaPhoneNumber, form) as TextAreaFormItem).value;
    const message = (controller.getItem(AskForHelpAsGuestFormIds.TextAreaMessage, form) as TextAreaFormItem).value;

    return {
        action: "ask for help",
        name,
        email,
        phoneNumber,
        message,
    };
};
